import React from 'react';

import { useAuth0, Auth0ContextInterface, withAuthenticationRequired } from '@auth0/auth0-react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { userSelector } from '../reducers/current-user-selectors';

import { getEndToEndUserToken } from '../end-to-end/get-end-to-end-user-tokens';

import { getLegacyTokens } from './get-legacy-tokens';
import { registerUser } from './register-user';

import { isProd } from '../config/config';
import { PathParamSpy } from '../widgets/browser-history-spy';
import { LoadingMessage } from '../widgets/loading-message';
import { NotFoundPage } from '../widgets/not-found-page';
import { PermissionMessage } from '../widgets/permission-message';

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  registerCurrentUser: () => registerUser(dispatch),
});

const mapStateToProps = (state: any) => ({
  user: userSelector(state),
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type UserProviderProps = ReduxProps &
  DispatchProps & {
    children: React.ReactNode;
    requiresAdmin?: boolean;
    requiredHiringPermission?: string | string[];
    requiredEmployeesPermission?: string | string[];
  };

export class UserSessionAccessor {
  static _useAuth0: Auth0ContextInterface;

  static getSession = () => UserSessionAccessor._useAuth0;
}

export const UserProviderFactory = () => {
  // Turn off Auth0 authentication when we are local and have a end-to-end user
  // piloting the software
  if (!isProd) {
    const endToEndToken = getEndToEndUserToken();
    if (endToEndToken) {
      return UserProvider;
    }
  }

  // Technically, this could probably just return the `withAuthenticationRequired`
  // directly (since Cognito has long since been defunct). Going to leave for now
  // to be on the safe side in case there are edge cases out there
  const legacyTokens = getLegacyTokens();
  if (!legacyTokens) {
    return withAuthenticationRequired(UserProvider);
  }
  return UserProvider;
};

/**
 * This component will sit in the component tree and ensure that we have a user, that
 * the user is authenticated, and that the user has the right roles for going to this
 * route
 */
const UserProvider = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    user,
    requiresAdmin,
    requiredHiringPermission,
    requiredEmployeesPermission,
    children,
    registerCurrentUser,
  }: UserProviderProps) => {
    // During the transition, we're going to check for Cognito tokens that haven't expired
    // yet. If we have those, then use them. If not, make the user log in with Auth0
    const legacyTokens = getLegacyTokens();
    const endToEndToken = getEndToEndUserToken();
    if (endToEndToken && !isProd) {
      if (!user) {
        registerCurrentUser();
      }
    } else if (!legacyTokens) {
      UserSessionAccessor._useAuth0 = useAuth0();
      const { isAuthenticated } = UserSessionAccessor._useAuth0;
      // If we have forced authenticate but don't have a user in the store, then we need to
      // make a call to register this user
      if (isAuthenticated && !user) {
        registerCurrentUser();
      }
    } else if (!user) {
      registerCurrentUser();
    }

    // If we've gotten past the above block with no user, then wait around for the user to come in
    if (!user) {
      return <LoadingMessage />;
    }

    if (!user.isAdmin && requiresAdmin) {
      return <NotFoundPage />;
    }

    if (!user.isAdmin && requiredHiringPermission) {
      const hiringPermission = Array.isArray(requiredHiringPermission)
        ? requiredHiringPermission
        : [requiredHiringPermission];
      if (hiringPermission.indexOf(user.hiringPermission) < 0) {
        return <PermissionMessage />;
      }
    }

    if (!user.isAdmin && requiredEmployeesPermission) {
      const employeesPermission = Array.isArray(requiredEmployeesPermission)
        ? requiredEmployeesPermission
        : [requiredEmployeesPermission];
      if (employeesPermission.indexOf(user.employeesPermission) < 0) {
        return <PermissionMessage />;
      }
    }

    // Return the children if the permissions match
    return (
      <>
        {/*
          This is a little hacky - but this is the top of the AuthenticatedRoute tree, which
          makes it the first place we can capture the currently matched path parameters.
          The reason we want to spy on these at all is so that we can access them in the
          ProductTelemetryController without needing to put it into a provider, and react-router
          doesn't expose this information in a static way.
        */}
        <PathParamSpy />
        {children}
      </>
    );
  },
);
